import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { Flame } from "@fleet/icons";
import { AppBackground } from "@/components/app-background";
import { Logo } from "@/components/ui/logo";

/**
 * GymLog's OWN auth shell — deliberately NOT the shared `AuthLayout` + `AuthCard`
 * frame (centered glass card inside a split brand panel) that every clone
 * inherited and which therefore read as "the same template".
 *
 * GymLog breaks the silhouette with an **energetic full-bleed gym scene**:
 *
 *   ┌──────────────────────────────┐
 *   │ ▓▓▓ plates climbing  · chip · │  ← hero band: accent strip, plates motif,
 *   │ ▓ ◳ GYMlog                    │     eyebrow chip + wordmark
 *   │ ▓ BIG UPPERCASE HEADLINE      │  ← display-font promise (page h1)
 *   │ ▓ supporting line             │
 *   │ ──────────────────────────    │  ← hairline rule (no card border)
 *   │  email   [____________]       │  ← form is FLAT, full-bleed on the bg
 *   │  pass    [____________]       │     (no GlassSurface card around it)
 *   │  [ SIGN IN ]                  │
 *   └──────────────────────────────┘
 *
 * No glass card, no two-column split, no per-screen variant matrix: a single
 * energetic editorial column where the brand hero sits on a solid-accent band
 * (the gym "rack" strip) and the form pours in below, flat on the ambient
 * background. Structurally unlike listto's plain stacked column AND unlike the
 * old gym card/split, even though we still share primitives (buttons, fields,
 * SocialAuthButtons, disabled-until-ready). Screens pour their fields in as
 * `children`.
 *
 * ── SCROLL CONTRACT (kit #13, same fix as melodimals #4) ───────────────────
 * `AppBackground` is `position: fixed; overflow: hidden` and clips its children,
 * so it is used here as a pure backdrop SIBLING. The content lives in its own
 * scroll container: `min-h-[100dvh] overflow-y-auto` (never `h-dvh` /
 * `overflow-hidden`), with a `min-h-0` / `min-h-full` inner column, so a tall
 * form (register) or a short landscape viewport is always fully reachable by
 * scrolling. Safe-area insets are honored on the scroll container.
 */

interface AuthSceneProps {
  /** Eyebrow chip on the solid-accent band (e.g. "Back in the gym"). Optional. */
  eyebrow?: JSX.Element;
  /** Hero headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: JSX.Element;
  /** Supporting line under the headline. */
  tagline?: JSX.Element;
  /**
   * Heading level for the headline. The hero headline IS the page h1 for
   * GymLog's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the column. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered flat below the hero band. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

const HEADING_CLASS =
  "text-balance text-2xl uppercase leading-tight tracking-tight text-foreground sm:text-3xl";
const HEADING_STYLE = {
  "font-family": "var(--font-display)",
  "font-weight": "700",
} as const;

export function AuthScene(props: AuthSceneProps) {
  const heading = (children: JSX.Element) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class={HEADING_CLASS} style={HEADING_STYLE}>
        {children}
      </h2>
    ) : (
      <h1 class={HEADING_CLASS} style={HEADING_STYLE}>
        {children}
      </h1>
    );

  return (
    <>
      <AppBackground />

      {/*
        NOTE: gym-log's `html, body` are `overflow: hidden; height: 100%`
        (global.css), so the WINDOW never scrolls — the scroll must live INSIDE
        this container. Hence `h-[100dvh]` (a BOUNDED height) + `overflow-y-auto`:
        the container itself becomes the scroller, so a tall form (register) or a
        short landscape viewport overflows WITHIN it and is reachable by scrolling
        — never clipped. (The kit's `min-h-[100dvh]` assumes a scrollable body,
        which gym-log does not have.)
      */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(2rem, env(safe-area-inset-top))",
          "padding-bottom": "max(2rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-8"
          classList={{
            "max-w-sm": (props.width ?? "narrow") === "narrow",
            "max-w-md": props.width === "wide",
          }}
        >
          {/* Hero band — energetic accent strip with plates motif, eyebrow chip
              and wordmark, then the display-font promise. */}
          <header class="relative overflow-hidden rounded-3xl border-l-4 border-accent bg-card/40 px-6 py-7 sm:px-7">
            <PlatesProgressBackdrop />

            <div class="relative z-10 flex flex-col gap-5">
              <div class="flex items-center justify-between gap-3">
                <Link
                  to="/"
                  class="rounded-xl outline-none transition-transform hover:scale-105 active:scale-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="GymLog"
                >
                  <Logo size="sm" />
                </Link>
                <Show when={props.eyebrow}>
                  <span class="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent-foreground shadow-sm ring-1 ring-black/5">
                    <Flame class="h-3.5 w-3.5" aria-hidden="true" />
                    {props.eyebrow}
                  </span>
                </Show>
              </div>

              <Show when={props.headline}>
                <div class="space-y-2">
                  {heading(props.headline)}
                  <Show when={props.tagline}>
                    <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                      {props.tagline}
                    </p>
                  </Show>
                </div>
              </Show>
            </div>
          </header>

          {/* Hairline rule instead of a card border. */}
          <div class="my-7 h-px w-full bg-border/70" />

          {/* Form lives flat / full-bleed on the ambient background (no card). */}
          <div class="w-full">{props.children}</div>

          <Show when={props.footer}>
            <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}

/**
 * Ambient strength backdrop: stacked plates rising left-to-right like a progress
 * chart, crowned by a dumbbell silhouette, plus a soft accent bloom. Purely
 * decorative, theme-token only (never hardcoded colors), so it stays legible
 * under any theme.
 */
function PlatesProgressBackdrop() {
  return (
    <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />

      {/* Climbing plates = progress. Each bar a notch taller than the last. */}
      <svg
        class="absolute -bottom-2 right-1 h-40 w-56 opacity-[0.16]"
        viewBox="0 0 200 140"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="8" y="96" width="26" height="40" rx="6" class="fill-accent" />
        <rect x="44" y="74" width="26" height="62" rx="6" class="fill-accent" />
        <rect x="80" y="50" width="26" height="86" rx="6" class="fill-accent" />
        <rect x="116" y="28" width="26" height="108" rx="6" class="fill-accent" />
        <path
          d="M21 92 L57 70 L93 46 L129 24 L168 10"
          class="stroke-foreground/40"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
        <circle cx="168" cy="10" r="5" class="fill-foreground/50" />
      </svg>
    </div>
  );
}
